import { useMemo } from 'react';

import { WorkflowType } from '@/constants/dispositions';
import type { FieldGroup, SelectOption } from '@/types/formSchema';

import { usePayors } from './usePayors';

// Workflow types whose `primary_payer_name` field is enhanced with the live payor list.
const PAYOR_ENHANCED_WORKFLOW_TYPES: ReadonlySet<string> = new Set([
  WorkflowType.UNIFIEDMEDBV,
  WorkflowType.BHO,
  WorkflowType.MEDBHOBV,
  WorkflowType.PA_STATUS_PBM,
  WorkflowType.PA_STATUS_MEDBV,
]);

/**
 * Enhances inputGroups by replacing the `primary_payer_name` field's static options
 * with live payor data from the PayorService API.
 *
 * Only active for payor-enhanced workflows (unifiedmedbv, pa_status_pbm, pa_status_medbv). For all other
 * workflows, returns input unchanged with zero API overhead (usePayors receives enabled=false).
 *
 * Falls back to original static options if the API fails or returns empty data.
 */
export function usePayorEnhancedGroups(
  inputGroups: FieldGroup[],
  workflowType?: string,
): { inputGroups: FieldGroup[]; isPayorsLoading: boolean } {
  const isPayorEnhanced = !!workflowType && PAYOR_ENHANCED_WORKFLOW_TYPES.has(workflowType);
  const { payors, isLoading } = usePayors(isPayorEnhanced);

  const enhancedGroups = useMemo(() => {
    // Fast path: not a payor-enhanced workflow or no payors loaded yet
    if (!isPayorEnhanced || payors.length === 0) {
      return inputGroups;
    }

    // Build select options from payors: label=name, value=slug
    const payorOptions: SelectOption[] = payors
      .filter(p => p.name && p.slug)
      .map(p => ({ label: p.name!, value: p.slug! }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Don't replace if we got empty options
    if (payorOptions.length === 0) {
      return inputGroups;
    }

    // Replace options for payor fields: legacy schemas use the
    // `primary_payer_name` id; newer PA-status schemas mark the field with
    // type "payor" (id `payor_name`).
    return inputGroups.map(group => ({
      ...group,
      fields: group.fields.map(field => {
        if (field.id === 'primary_payer_name' || field.type === 'payor') {
          return { ...field, options: payorOptions };
        }
        return field;
      }),
    }));
  }, [isPayorEnhanced, inputGroups, payors]);

  return {
    inputGroups: enhancedGroups,
    isPayorsLoading: isPayorEnhanced && isLoading,
  };
}
